import { gql } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { PlaylistCreateInput } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { useCreatePlaylistMutation } from './__generated__/CreatePlaylistModal.graphql';

const FormData = z.object({
  customerId: z.string(),
  title: z
    .string()
    .min(1)
    .regex(/.*\S.*/, t('titleSpaceWarn')),
  description: z.string().optional(),
});
type FormData = z.infer<typeof FormData>;

interface Props {
  customerId: string;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (id: string) => Promise<void> | void;
  playlistType: PlaylistCreateInput['playlistType'];
}

export function CreatePlaylistModal({
  customerId,
  isOpen,
  onCancel,
  onSuccess,
  playlistType,
}: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <CreatePlaylistModalContent
          customerId={customerId}
          initialFocusRef={initialFocusRef}
          onCancel={onCancel}
          onSuccess={onSuccess}
          playlistType={playlistType}
        />
      </ModalContent>
    </Modal>
  );
}

type ContentProps = {
  customerId: string;
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  onCancel: () => void;
  onSuccess: (id: string) => Promise<void> | void;
  playlistType: PlaylistCreateInput['playlistType'];
};

function CreatePlaylistModalContent({
  customerId,
  initialFocusRef,
  onCancel,
  onSuccess,
  playlistType,
}: ContentProps) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      customerId,
      title: '',
    },
    resolver: zodResolver(FormData),
    mode: 'onChange',
  });
  const { ref: titleInputRef, ...titleInputProps } = register('title');

  const [createPlaylist] = useCreatePlaylistMutation();

  const onSubmit = useCallback(
    async ({ customerId, title, description }: FormData) => {
      try {
        const { data } = await createPlaylist({
          variables: {
            input: {
              customerId,
              title,
              description,
              playlistType,
            },
          },
        });

        if (!data || !data.playlistCreate.playlist) {
          throw new Error('Create playlist failed');
        }

        await onSuccess?.(data.playlistCreate.playlist.id);
      } catch (err) {
        toast({
          status: 'error',
          title: t('createPlaylistErr'),
          description: fromError(err, 'CreatePlaylist'),
        });
      }
    },
    [toast, createPlaylist, onSuccess, playlistType],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>{t('createPlaylist')}</ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      <ModalBody>
        <VStack>
          <FormControl isRequired isInvalid={Boolean(errors.title)}>
            <FormLabel>{t('playlistTitle')}</FormLabel>
            <Input
              ref={(r) => {
                titleInputRef(r);
                initialFocusRef.current = r;
              }}
              placeholder={t('playlistTitlePlaceholder')}
              {...titleInputProps}
            />
            <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.description)}>
            <FormLabel>{t('description')}</FormLabel>
            <Input
              type="textarea"
              placeholder={t('playlistDescPlaceholder')}
              {...register('description')}
            />
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </VStack>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" colorScheme="blue" onClick={onCancel} isDisabled={isSubmitting}>
          {t('close')}
        </Button>
        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          type="submit"
          isDisabled={!isDirty || isSubmitting || Object.keys(errors).length > 0}
          isLoading={isSubmitting}
        >
          {t('create')}
        </Button>
      </ModalFooter>
    </form>
  );
}

CreatePlaylistModal.graphql = {
  mutations: {
    CreatePlaylist: gql`
      mutation CreatePlaylist($input: PlaylistCreateInput!) {
        playlistCreate(input: $input) {
          playlist {
            id
            ...PlaylistTable_playlist
          }
        }
      }
    `,
  },
};
